import { useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/styled/Card';
import { SecondaryButton } from '../../../components/styled/Button';
import { Flex } from '../../../components/styled/Layout';
import hestonService from '../services/hestonService';
import './AddressDetailModal.css';

export default function AddressDetailModal({ address, onClose, onUpdate }) {
  const [pricingHistory, setPricingHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showComparisonTable, setShowComparisonTable] = useState(true);

  useEffect(() => {
    loadPricingHistory();
  }, [address.addressId]);

  const loadPricingHistory = async () => {
    try {
      setLoadingHistory(true);
      const history = await hestonService.getAddressHistory(address.addressId);
      setPricingHistory(history);
    } catch (err) {
      console.error('Error loading pricing history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Calculate price change between two values
  const calculateChange = (current, previous) => {
    if (!current || !previous) return null;

    const currentNum = parseFloat(String(current).replace(/[$,]/g, ''));
    const previousNum = parseFloat(String(previous).replace(/[$,]/g, ''));

    if (isNaN(currentNum) || isNaN(previousNum) || previousNum === 0) return null;

    const delta = currentNum - previousNum;
    const percentChange = ((delta / previousNum) * 100).toFixed(1);

    return {
      delta: delta.toFixed(2),
      percent: percentChange,
      direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'same'
    };
  };

  // Get change indicator
  const getChangeIndicator = (change) => {
    if (!change) return null;

    const color = change.direction === 'up' ? '#28A745' : change.direction === 'down' ? '#C0392B' : '#999999';
    const arrow = change.direction === 'up' ? '↑' : change.direction === 'down' ? '↓' : '→';

    return (
      <span style={{ color, fontSize: '13px', fontWeight: 'bold' }}>
        {arrow} ${Math.abs(change.delta)} ({change.percent > 0 ? '+' : ''}{change.percent}%)
      </span>
    );
  };

  return (
    <div className="address-modal-overlay" onClick={handleOverlayClick}>
      <div className="address-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="address-modal-close" onClick={onClose}>
          ✕ Close
        </button>

        {/* Address Details */}
        <div className="address-modal-header">
          <div className="address-info">
            <div className="address-info-item">
              <span className="address-label">Address:</span>
              <span className="address-value">{address.address}</span>
            </div>
            <div className="address-info-item">
              <span className="address-label">City:</span>
              <span className="address-value">{address.city}</span>
            </div>
            <div className="address-info-item">
              <span className="address-label">State:</span>
              <span className="address-value">{address.state}</span>
            </div>
            <div className="address-info-item">
              <span className="address-label">ZIP Code:</span>
              <span className="address-value">{address.zipCode}</span>
            </div>
            {address.branch && (
              <div className="address-info-item">
                <span className="address-label">Branch:</span>
                <span className="address-value">{address.branch}</span>
              </div>
            )}
            {address.buildingSqFt && (
              <div className="address-info-item">
                <span className="address-label">Building Sq. Ft.:</span>
                <span className="address-value">{address.buildingSqFt}</span>
              </div>
            )}
          </div>
        </div>

        {/* View Toggle */}
        <div className="address-modal-tabs">
          <button
            className={`address-modal-tab ${showComparisonTable ? 'active' : ''}`}
            onClick={() => setShowComparisonTable(true)}
          >
            📋 Comparison Table
          </button>
          <button
            className={`address-modal-tab ${!showComparisonTable ? 'active' : ''}`}
            onClick={() => setShowComparisonTable(false)}
          >
            📊 Timeline View
          </button>
        </div>

        {/* Content Area */}
        <div className="address-modal-body">
          {loadingHistory ? (
            <div className="address-modal-loading">
              <span className="spinner-inline"></span>
              <span>Loading history...</span>
            </div>
          ) : pricingHistory.length === 0 ? (
            <div className="address-modal-empty">
              <p>No pricing history available</p>
            </div>
          ) : showComparisonTable ? (
            /* Comparison Table */
            <div className="address-table-wrapper">
              <table className="address-comparison-table">
                <thead>
                  <tr>
                    <th className="address-table-field-col">Pricing Field</th>
                    {pricingHistory.map((entry, index) => (
                      <th key={index}>
                        {new Date(entry.timeOfScrape).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {index === 0 && <span className="latest-tag">Latest</span>}
                      </th>
                    ))}
                    <th>Overall Trend</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="address-table-field-col">Pest365</td>
                    {pricingHistory.map((entry, index) => {
                      const prevEntry = pricingHistory[index + 1];
                      const change = prevEntry ? calculateChange(entry.pricing?.pest365, prevEntry.pricing?.pest365) : null;
                      return (
                        <td key={index} className="address-table-price-cell">
                          <div>{hestonService.formatCurrency(entry.pricing?.pest365)}</div>
                          {change && <div className="change-indicator">{getChangeIndicator(change)}</div>}
                        </td>
                      );
                    })}
                    <td className="address-table-trend-cell">
                      {pricingHistory.length > 1 && (() => {
                        const overallChange = calculateChange(
                          pricingHistory[0].pricing?.pest365,
                          pricingHistory[pricingHistory.length - 1].pricing?.pest365
                        );
                        return overallChange ? getChangeIndicator(overallChange) : '→';
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <td className="address-table-field-col">Pest365+</td>
                    {pricingHistory.map((entry, index) => {
                      const prevEntry = pricingHistory[index + 1];
                      const change = prevEntry ? calculateChange(entry.pricing?.pest365Plus, prevEntry.pricing?.pest365Plus) : null;
                      return (
                        <td key={index} className="address-table-price-cell">
                          <div>{hestonService.formatCurrency(entry.pricing?.pest365Plus)}</div>
                          {change && <div className="change-indicator">{getChangeIndicator(change)}</div>}
                        </td>
                      );
                    })}
                    <td className="address-table-trend-cell">
                      {pricingHistory.length > 1 && (() => {
                        const overallChange = calculateChange(
                          pricingHistory[0].pricing?.pest365Plus,
                          pricingHistory[pricingHistory.length - 1].pricing?.pest365Plus
                        );
                        return overallChange ? getChangeIndicator(overallChange) : '→';
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <td className="address-table-field-col">One-Time</td>
                    {pricingHistory.map((entry, index) => {
                      const prevEntry = pricingHistory[index + 1];
                      const change = prevEntry ? calculateChange(entry.pricing?.oneTime, prevEntry.pricing?.oneTime) : null;
                      return (
                        <td key={index} className="address-table-price-cell">
                          <div>{hestonService.formatCurrency(entry.pricing?.oneTime)}</div>
                          {change && <div className="change-indicator">{getChangeIndicator(change)}</div>}
                        </td>
                      );
                    })}
                    <td className="address-table-trend-cell">
                      {pricingHistory.length > 1 && (() => {
                        const overallChange = calculateChange(
                          pricingHistory[0].pricing?.oneTime,
                          pricingHistory[pricingHistory.length - 1].pricing?.oneTime
                        );
                        return overallChange ? getChangeIndicator(overallChange) : '→';
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <td className="address-table-field-col">Initial Service Visit</td>
                    {pricingHistory.map((entry, index) => {
                      const prevEntry = pricingHistory[index + 1];
                      const change = prevEntry ? calculateChange(entry.pricing?.initialServiceVisit, prevEntry.pricing?.initialServiceVisit) : null;
                      return (
                        <td key={index} className="address-table-price-cell">
                          <div>{hestonService.formatCurrency(entry.pricing?.initialServiceVisit)}</div>
                          {change && <div className="change-indicator">{getChangeIndicator(change)}</div>}
                        </td>
                      );
                    })}
                    <td className="address-table-trend-cell">
                      {pricingHistory.length > 1 && (() => {
                        const overallChange = calculateChange(
                          pricingHistory[0].pricing?.initialServiceVisit,
                          pricingHistory[pricingHistory.length - 1].pricing?.initialServiceVisit
                        );
                        return overallChange ? getChangeIndicator(overallChange) : '→';
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <td className="address-table-field-col">Mosquito Tick Control</td>
                    {pricingHistory.map((entry, index) => {
                      const prevEntry = pricingHistory[index + 1];
                      const change = prevEntry ? calculateChange(entry.pricing?.mosquitoTickControl, prevEntry.pricing?.mosquitoTickControl) : null;
                      const formattedValue = hestonService.formatCurrency(entry.pricing?.mosquitoTickControl);
                      return (
                        <td key={index} className="address-table-price-cell">
                          <div>
                            {formattedValue === 'Not Available' ? (
                              <span style={{ color: '#999' }}>Not Available</span>
                            ) : formattedValue ? (
                              formattedValue
                            ) : (
                              <span style={{ color: '#999' }}>No data</span>
                            )}
                          </div>
                          {change && <div className="change-indicator">{getChangeIndicator(change)}</div>}
                        </td>
                      );
                    })}
                    <td className="address-table-trend-cell">
                      {pricingHistory.length > 1 && (() => {
                        const overallChange = calculateChange(
                          pricingHistory[0].pricing?.mosquitoTickControl,
                          pricingHistory[pricingHistory.length - 1].pricing?.mosquitoTickControl
                        );
                        return overallChange ? getChangeIndicator(overallChange) : '→';
                      })()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            /* Timeline View */
            <div className="address-timeline">
              {pricingHistory.map((entry, index) => {
                const previousEntry = pricingHistory[index + 1];
                const isLatest = index === 0;

                return (
                  <div key={index} className={`address-timeline-entry ${isLatest ? 'latest' : ''}`}>
                    <div className="address-timeline-date">
                      {hestonService.formatDate(entry.timeOfScrape)}
                      {isLatest && <span className="latest-badge">Latest</span>}
                    </div>
                    <div className="address-timeline-pricing">
                      <div className="address-timeline-price-row">
                        <span className="address-timeline-label">Pest365:</span>
                        <span className="address-timeline-value">{hestonService.formatCurrency(entry.pricing?.pest365)}</span>
                        {previousEntry && getChangeIndicator(calculateChange(entry.pricing?.pest365, previousEntry.pricing?.pest365))}
                      </div>
                      <div className="address-timeline-price-row">
                        <span className="address-timeline-label">Pest365+:</span>
                        <span className="address-timeline-value">{hestonService.formatCurrency(entry.pricing?.pest365Plus)}</span>
                        {previousEntry && getChangeIndicator(calculateChange(entry.pricing?.pest365Plus, previousEntry.pricing?.pest365Plus))}
                      </div>
                      <div className="address-timeline-price-row">
                        <span className="address-timeline-label">One-Time:</span>
                        <span className="address-timeline-value">{hestonService.formatCurrency(entry.pricing?.oneTime)}</span>
                        {previousEntry && getChangeIndicator(calculateChange(entry.pricing?.oneTime, previousEntry.pricing?.oneTime))}
                      </div>
                      <div className="address-timeline-price-row">
                        <span className="address-timeline-label">Initial Service Visit:</span>
                        <span className="address-timeline-value">{hestonService.formatCurrency(entry.pricing?.initialServiceVisit)}</span>
                        {previousEntry && getChangeIndicator(calculateChange(entry.pricing?.initialServiceVisit, previousEntry.pricing?.initialServiceVisit))}
                      </div>
                      <div className="address-timeline-price-row">
                        <span className="address-timeline-label">Mosquito Tick Control:</span>
                        <span className="address-timeline-value">
                          {(() => {
                            const formatted = hestonService.formatCurrency(entry.pricing?.mosquitoTickControl);
                            return formatted === 'Not Available' ? (
                              <span style={{ color: '#999' }}>Not Available</span>
                            ) : formatted ? (
                              formatted
                            ) : (
                              <span style={{ color: '#999' }}>No data</span>
                            );
                          })()}
                        </span>
                        {previousEntry && getChangeIndicator(calculateChange(entry.pricing?.mosquitoTickControl, previousEntry.pricing?.mosquitoTickControl))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
